import React, { useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { CustomChevronCircleIcon } from "../../../assets/icon";
import { PreassessmentQuestion } from "../../../axios/quiz";
import { CancelIcon } from "../../../assets/icon";
import { HeaderQuiz } from "./header-quiz/header-quiz";
import { QuizMainBlock } from "./quiz-main-block/quiz-main-block";

import {
  containerStyle,
  headerBoxStyle,
  closeButtonStyle,
  contentBoxStyle,
  footerBoxStyle,
  footerActionsStyle,
  managerBypassBoxStyle,
  managerBypassHintStyle,
  managerBypassWaitingBoxStyle,
  managerBypassWaitingHintStyle,
  managerBypassButtonStyle,
  buttonStyle,
  iconBoxStyle,
} from "./styles";
import { useQuizeStore } from "../../../stores/quizeStore";
import { useUserStore } from "../../../stores/userStore";

interface QuizContentProps {
  currentQuestion: PreassessmentQuestion;
  currentQuestionIndex: number;
  totalQuestions: number;
  selectedAnswer: string;
  isAnswerChecked: boolean;
  isAnswerSelected: boolean;
  isSubmitting: boolean;
  isCompletingPreassessment?: boolean;
  onAnswerSelect: (answer: string) => void;
  onCheckAnswer: () => void;
  onNextQuestion: () => void;
  onFinish: () => void;
  onClose: () => void;
  onCompletePreassessment?: () => void;
  isCheckAnswerBlocked?: boolean;
  connectionErrorMessage?: string | null;
  connectionRestored?: boolean;
}

export const QuizContent: React.FC<QuizContentProps> = ({
  currentQuestion,
  currentQuestionIndex,
  totalQuestions,
  selectedAnswer,
  isAnswerChecked,
  isAnswerSelected,
  isSubmitting,
  isCompletingPreassessment,
  onAnswerSelect,
  onCheckAnswer,
  onNextQuestion,
  onFinish,
  onClose,
  onCompletePreassessment,
  isCheckAnswerBlocked,
  connectionErrorMessage,
  connectionRestored,
}) => {
  const theme = useTheme();
  const { needCloseButton, toggleNeedCloseButton } = useQuizeStore();
  const isManager = useUserStore((state) => Boolean(state.user?.is_manager));
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [currentQuestionIndex, currentQuestion]);

  const isBusy = isSubmitting || Boolean(isCompletingPreassessment);
  const isCheckAnswerDisabled =
    !isAnswerSelected || isBusy || Boolean(isCheckAnswerBlocked);
  const showManagerBypass =
    isManager && Boolean(onCompletePreassessment) && !isCheckAnswerBlocked;
  const isManagerBypassDisabled = isBusy;

  return (
    <Box sx={containerStyle}>
      {connectionErrorMessage ? (
        <Typography
          variant="body1"
          sx={{
            color: "error.main",
            textAlign: "center",
            fontWeight: 700,
            fontSize: { xs: "15px", sm: "16px" },
            mt: 1.5,
            mb: 2,
            px: 1,
          }}
        >
          {connectionErrorMessage}
        </Typography>
      ) : null}
      {connectionRestored ? (
        <Typography
          variant="body1"
          sx={{
            color: "success.main",
            textAlign: "center",
            fontWeight: 700,
            fontSize: { xs: "15px", sm: "16px" },
            mt: 1.5,
            mb: 2,
            px: 1,
          }}
        >
          Connection restored. You can continue the pre-assessment.
        </Typography>
      ) : null}
      {isCompletingPreassessment ? (
        <Box sx={managerBypassWaitingBoxStyle}>
          <CircularProgress size={22} sx={{ color: "#06B6D4" }} />
          <Typography variant="body1" sx={managerBypassWaitingHintStyle}>
            Please wait — submitting answers for the remaining questions. This
            may take a moment.
          </Typography>
        </Box>
      ) : null}
      <Box sx={headerBoxStyle}>
        <HeaderQuiz
          progress={Math.round(
            ((currentQuestionIndex + (isAnswerChecked ? 1 : 0)) /
              totalQuestions) *
              100
          )}
          totalAnswers={totalQuestions}
        />
        {needCloseButton && (
          <Box
            onClick={() => {
              onClose();
              toggleNeedCloseButton();
            }}
            sx={closeButtonStyle}
          >
            <CancelIcon />
          </Box>
        )}
      </Box>
      <Box sx={contentBoxStyle} ref={containerRef}>
        <QuizMainBlock
          question={currentQuestion}
          questionIndex={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isAnswerChecked={isAnswerChecked}
          hideAnswerPoints
        />
      </Box>
      <Box sx={footerBoxStyle}>
        <Box sx={footerActionsStyle}>
          {showManagerBypass ? (
            <Box sx={managerBypassBoxStyle}>
              <Typography sx={managerBypassHintStyle}>
                {isCompletingPreassessment
                  ? "Please wait while each remaining answer is sent to the server."
                  : "Managers can skip the remaining questions. This submits the top score (5 points) for each unanswered question."}
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={() => {
                  if (isManagerBypassDisabled) return;
                  onCompletePreassessment?.();
                }}
                disabled={isManagerBypassDisabled}
                sx={managerBypassButtonStyle(isManagerBypassDisabled)}
                startIcon={
                  isCompletingPreassessment ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : undefined
                }
              >
                {isCompletingPreassessment
                  ? "Please wait..."
                  : "Complete the pre-assessment"}
              </Button>
            </Box>
          ) : (
            <Box />
          )}
          {!isAnswerChecked ? (
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                if (isCheckAnswerDisabled) return;
                onCheckAnswer();
              }}
              disabled={isCheckAnswerDisabled}
              sx={buttonStyle(theme, isCheckAnswerDisabled)}
            >
              Check Answer
              <Box sx={iconBoxStyle}>
                <CustomChevronCircleIcon />
              </Box>
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={
                currentQuestionIndex < totalQuestions - 1
                  ? onNextQuestion
                  : onFinish
              }
              disabled={isBusy}
              sx={buttonStyle(theme, isBusy)}
            >
              {currentQuestionIndex < totalQuestions - 1
                ? "Next question"
                : "Finish"}
              <Box sx={iconBoxStyle}>
                <CustomChevronCircleIcon />
              </Box>
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default QuizContent;
